import React, { useState, useRef, useEffect, useContext } from 'react'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { Dropdown } from 'primereact/dropdown'
import { Button } from 'primereact/button'
import { Context } from '../../Context'
import { GLOBALS } from '../../utils/Globals'
import { useInputValue } from '../../hooks/useInputValue'
import { VirtualMachineType } from '../../components/VirtualMachineType'
import { VirtualMachineConnectionType } from '../../components/VirtualMachineConnectionType'
import { Growl } from 'primereact/growl'
import './styles.scss'
import { VirtualMachineService } from '../../services/VirtualMachine'

export const VirtualMachineForm = ({ item, isLoading, onSubmit, cancelLink }) => {
  const { isAuth: token } = useContext(Context)
  const [typev, setTypev] = useState(item ? item.type.id : '')
  const [connectionType, setConnectionType] = useState(item ? item.connection_type : '')
  const [isPasswordVisible, setPasswordVisible] = useState(false)
  const [isPhysical, setIsPhysical] = useState(item ? item.is_physical : false)
  const growlRef = useRef(null)

  const [folderVMs, setFolderVMs] = useState([]) // For VMs in the folder

  const [inputValue, setInputValue] = useState(item ? item.display_name : '') // Texto del Input
  const [showSuggestions, setShowSuggestions] = useState(false) // Control de sugerencias
  const [filteredVMs, setFilteredVMs] = useState([]) // Opciones filtradas según el texto
  const [, setEditableValue] = useState(item ? item.display_name : '')

  const handleVMInput = (e) => {
    setEditableValue(e.target.value) // Solo actualiza el texto ingresado
  }

  useEffect(() => {
    // Load VMs in folder on component mount
    const loadFolderVMs = async () => {
      try {
        const folderData = await VirtualMachineService.getVMsInFolder(token)
        setFolderVMs(
          folderData.vms.map((vm) => ({
            label: vm,
            value: vm,
          }))
        )
      } catch (error) {
        showGrowl('error', 'Error', 'No se pudieron cargar las máquinas virtuales.')
      }
    }

    loadFolderVMs()
  }, [])

  useEffect(() => {
    setIsPhysical(item ? item.is_physical : false)
  }, [item])

  const handleInputChange = async (e) => {
    const newValue = e.target.value
    setInputValue(newValue)

    // Sincronizar con display_name
    display_name.onChange({ target: { value: newValue } })

    // Filtrar sugerencias según el texto ingresado
    const filtered = folderVMs.filter((vm) =>
      vm.label.toLowerCase().includes(newValue.toLowerCase())
    )
    setFilteredVMs(filtered)
    setShowSuggestions(filtered.length > 0)

    // Si coincide exactamente con una opción, cargar los detalles
    const exactMatch = folderVMs.find((vm) => vm.label.toLowerCase() === newValue.toLowerCase())
    if (exactMatch) {
      try {
        const vmDetails = await VirtualMachineService.getVMDetails(exactMatch.value, token)
        const vmDetailsHtml = formatVmDetails(vmDetails)
        showGrowl('info', 'Detalles del recurso', vmDetailsHtml, 50000)
      } catch (error) {
        showGrowl('error', 'Error', 'No se pudieron cargar los detalles de la máquina virtual.')
      }
    }
  }

  const handleSuggestionClick = async (vm) => {
    setInputValue(vm.label) // Actualizar el texto
    setShowSuggestions(false) // Ocultar sugerencias

    // Sincronizar con display_name
    display_name.onChange({ target: { value: vm.label } })

    // Obtener detalles de la máquina seleccionada
    try {
      const vmDetails = await VirtualMachineService.getVMDetails(vm.value, token)
      const vmDetailsHtml = formatVmDetails(vmDetails)
      showGrowl('info', 'Detalles del recurso', vmDetailsHtml, 50000)
    } catch (error) {
      showGrowl('error', 'Error', 'No se pudieron cargar los detalles de la máquina virtual.')
    }
  }

  const formatVmDetails = (vmDetails) => {
    return (
      <ul>
        <li>
          <strong>Nombre:</strong> {vmDetails.vm_details.name}
        </li>
        <li>
          <strong>Estado de Encendido:</strong> {vmDetails.vm_details.power_state}
        </li>
        <li>
          <strong>Sistema Operativo:</strong>
          {vmDetails.vm_details.guest_os}
        </li>
        <li>
          <strong>CPU:</strong> {vmDetails.vm_details.cpu_count} núcleos
        </li>
        <li>
          <strong>Memoria:</strong> {vmDetails.vm_details.memory_mb} MB
        </li>
        <li>
          <strong>IP:</strong> {vmDetails.vm_details.ip_address || 'No asignada'}
        </li>
        <li>
          <strong>Tiempo de Actividad:</strong>{' '}
          {new Date(vmDetails.vm_details.uptime_seconds).toLocaleString()}
        </li>
        <li>
          <strong>Notas:</strong> {vmDetails.vm_details.notes}
        </li>
      </ul>
    )
  }

  const togglePasswordVisibility = () => {
    setPasswordVisible((prev) => !prev)
  }

  const showGrowl = (severity, summary, detail, life = 5000) => {
    growlRef.current.show({
      severity,
      summary,
      detail,
      life,
    })
  }

  const { ...ip } = useInputValue(
    {
      value: item ? item.ip : '',
      name: 'ip',
      type: 'text',
      required: true,
      disabled: isLoading,
    },
    false
  )

  const { ...display_name } = useInputValue(
    {
      value: item ? item.display_name : '',
      name: 'display_name',
      type: 'text',
      required: true,
      disabled: isLoading,
      maxLength: 20,
    },
    false
  )

  const { ...port } = useInputValue(
    {
      value: item ? item.port : '',
      name: 'port',
      type: 'text',
      required: true,
      disabled: isLoading,
    },
    false
  )

  const { ...username } = useInputValue(
    {
      value: item ? item.username : '',
      type: 'text',
      name: 'username',
      disabled: isLoading,
    },
    false
  )

  const { ...password } = useInputValue(
    {
      value: item ? item.password : '',
      type: isPasswordVisible ? 'text' : 'password',
      name: 'password',
      disabled: isLoading,
    },
    false
  )

  const { ...type } = useInputValue(
    {
      value: item ? item.type.id : '',
      name: 'type',
      disabled: isLoading,
      required: true,
    },
    false
  )

  const { ...connection_type } = useInputValue(
    {
      value: item ? item.connection_type : '',
      name: 'connection_type',
      disabled: isLoading,
    },
    false
  )

  // Nuevos campos para PDU
  const { ...host_address_pdu } = useInputValue(
    {
      value: item ? item.host_address_pdu : '',
      name: 'host_address_pdu',
      type: 'text',
      required: isPhysical,
      disabled: isLoading || !isPhysical,
    },
    false
  )

  const { ...port_pdu } = useInputValue(
    {
      value: item ? item.port_pdu : '',
      name: 'port_pdu',
      type: 'text',
      required: isPhysical,
      disabled: isLoading || !isPhysical,
    },
    false
  )

  const { ...details } = useInputValue(
    {
      value: item ? item.details : '',
      name: 'details',
      disabled: isLoading,
    },
    false
  )

  const typesList = Object.keys(GLOBALS.virtual_machine.types).map((k) => {
    return {
      label: GLOBALS.virtual_machine.types[k].name,
      value: GLOBALS.virtual_machine.types[k].id,
    }
  })

  const connectiontypesList = Object.keys(GLOBALS.virtual_machine.connectionType).map((k) => {
    return {
      label: GLOBALS.virtual_machine.connectionType[k].name,
      value: GLOBALS.virtual_machine.connectionType[k].id,
    }
  })

  const isPhysicalOptions = [
    { label: 'Sí', value: true },
    { label: 'No', value: false },
  ]

  const validateIpAddress = (ip) => {
    const ipv4Regex =
      /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/

    if (!ipv4Regex.test(ip)) {
      return false
    }

    return true
  }

  const typeItemTemplate = (option) => {
    if (!option.value) {
      return option.label
    } else {
      return (
        <div className="p-clearfix">
          <VirtualMachineType type={option.value} />
        </div>
      )
    }
  }

  const connectiontypeItemTemplate = (option) => {
    if (!option.value) {
      return option.label
    } else {
      return (
        <div className="p-clearfix">
          <VirtualMachineConnectionType type={option.value} />
        </div>
      )
    }
  }

  const changeSelectOptionHandler = (e) => {
    setTypev(e.value)
  }

  const changeSelectConecctionType = (e) => {
    setConnectionType(e.value)
  }

  const handleIsPhysicalChange = (e) => {
    setIsPhysical(e.value)
  }

  const hadleSubmit = (e) => {
    e.preventDefault()

    if (!validateIpAddress(ip.value)) {
      showGrowl('error', 'Error', 'La IP ingresada no es válida o está en un rango no permitido.')
      return
    }

    if (isPhysical && !validateIpAddress(host_address_pdu.value)) {
      showGrowl('error', 'Error', 'La dirección PDU ingresada no es válida.')
      return
    }

    const data = {
      ip: ip.value,
      port: port.value,
      username: username.value,
      password: password.value,
      type: typev,
      connection_type: connectionType,
      details: details.value,
      display_name: display_name.value,
      pod_id: null,
      is_physical: isPhysical ? 1 : 0,
      host_address_pdu: isPhysical ? host_address_pdu.value : null,
      port_pdu: isPhysical ? port_pdu.value : null,
    }

    if (data.type === '') {
      data.type = 'O'
    }

    onSubmit(data)
  }

  return (
    <div className="form-container">
      <Growl ref={growlRef}></Growl>
      <form disabled={isLoading} onSubmit={hadleSubmit}>
        <div className="form-group" style={{ position: 'relative' }}>
          <label htmlFor="vm-selector">Nombre para mostrar *</label>
          <InputText
            id="vm-selector"
            value={inputValue}
            onChange={handleInputChange}
            onInput={handleVMInput}
            placeholder="Escriba o seleccione una máquina virtual"
          />
          {showSuggestions && (
            <ul className="suggestions-list">
              {filteredVMs.map((vm, index) => (
                <li
                  key={index}
                  onClick={() => handleSuggestionClick(vm)}
                  className="suggestion-item"
                >
                  {vm.label}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="ip-input">IP {ip.required ? '*' : ''}</label>
          <InputText id="ip-input" {...ip} />
        </div>

        <div className="form-group">
          <label htmlFor="email-input">Puerto {port.required ? '*' : ''}</label>
          <InputText id="email-input" {...port} />
        </div>

        <div className="form-group">
          <label htmlFor="name-input">Nombre de usuario {username.required ? '*' : ''}</label>
          <InputText id="name-input" {...username} />
        </div>

        <div className="form-group">
          <label htmlFor="password-input">Contraseña {password.required ? '*' : ''}</label>
          <div style={{ position: 'relative' }}>
            <InputText id="password-input" {...password} />
            <span
              onClick={togglePasswordVisibility}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
              }}
            >
              {isPasswordVisible ? '🚫' : '👁️‍🗨️'}
            </span>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="type-input">Tipo {type.required ? '*' : ''}</label>

          <Dropdown
            id="type-input"
            options={typesList}
            autoWidth={false}
            value={typev}
            onChange={changeSelectOptionHandler}
            itemTemplate={typeItemTemplate}
          />
        </div>

        <div className="form-group">
          <label htmlFor="type-input">Tipo de conexión {connection_type.required ? '*' : ''}</label>

          <Dropdown
            id="connection-type-input"
            options={connectiontypesList}
            autoWidth={false}
            value={connectionType}
            onChange={changeSelectConecctionType}
            itemTemplate={connectiontypeItemTemplate}
          />
        </div>

        {/* Nuevo campo para is_physical */}
        <div className="form-group">
          <label htmlFor="is-physical-input">¿Es un equipo físico?</label>
          <Dropdown
            id="is-physical-input"
            options={isPhysicalOptions}
            value={isPhysical}
            onChange={handleIsPhysicalChange}
          />
        </div>

        {/* Campos condicionales para PDU */}
        {isPhysical && (
          <>
            <div className="form-group">
              <label htmlFor="host-address-pdu-input">Dirección PDU {isPhysical ? '*' : ''}</label>
              <InputText id="host-address-pdu-input" {...host_address_pdu} />
            </div>

            <div className="form-group">
              <label htmlFor="port-pdu-input">Puerto PDU {isPhysical ? '*' : ''}</label>
              <InputText id="port-pdu-input" {...port_pdu} />
            </div>
          </>
        )}

        <div className="form-group">
          <h3>Detalles</h3>
          <InputTextarea id="details-input" {...details} />
        </div>

        <div className="button-group">
          <Button
            label={isLoading ? 'Guardando...' : 'Guardar'}
            disabled={isLoading}
            type="submit"
            className="button primary"
          />
          <Link to={cancelLink} className="button secondary">
            Cancelar
          </Link>
        </div>
      </form>
    </div>
  )
}

VirtualMachineForm.propTypes = {
  item: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  cancelLink: PropTypes.string.isRequired,
}
